import React, { useEffect } from "react";
import { SchoolOfferLink } from "@/components/SchoolOfferLink";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  BookOpen,
  Clock,
  CheckCircle2,
  ArrowRight,
  Star,
  Sparkles,
  ShoppingCart,
  Download,
  Quote,
  Bell,
  CornerDownLeft,
} from "lucide-react";
import { NewFormatsShowcase } from "@/components/NewFormatsShowcase";
import { STATS } from "@/data/casPratiques";
import { KDP_URL, KDP_AVAILABLE, KDP_KINDLE_URL, KDP_KINDLE_AVAILABLE } from "@/lib/kdpConfig";

/**
 * Landing page for the KDP book "L'IA au service des enseignants".
 * Angle: individual teacher wanting to save 2 hours per day. Full
 * bottom-up marketing funnel — hero + 50-cases pitch + 3 sample
 * cases + author's promise + KDP purchase CTA + secondary CTA to
 * try SUX1 free.
 *
 * URL: /enseignants
 * SEO targets: "livre IA enseignants", "outil IA prof", "gagner temps
 * enseignant IA", "cas pratiques ChatGPT professeur".
 */

/**
 * Smart KDP CTA — points at Amazon when the book is live, or displays
 * a "Bientôt disponible" state (with waitlist mailto) while the ASIN
 * is still null. Keeps the visual weight of a real button so the page
 * doesn't look empty pre-launch.
 *
 * The `format` prop selects paperback (default, primary sale) or kindle
 * (e-book, cheaper alternative). Both point at Amazon.fr when live.
 */
const KdpCta = ({ variant, testId, format = "paperback" }) => {
  const isKindle = format === "kindle";
  const isPrimary = variant === "primary";
  const url = isKindle ? KDP_KINDLE_URL : KDP_URL;
  const available = isKindle ? KDP_KINDLE_AVAILABLE : KDP_AVAILABLE;
  const baseCls = isPrimary
    ? "bg-[#002FA7] text-white px-6 py-3 hover:bg-[#001A66]"
    : isKindle
      ? "border-2 border-white text-white px-8 py-4 hover:bg-white hover:text-[#002FA7]"
      : "bg-[#FF2A00] text-white px-8 py-4 hover:bg-white hover:text-[#002FA7]";
  const className = `inline-flex items-center justify-center gap-2 font-mono uppercase tracking-[0.14em] text-[11px] transition-colors ${baseCls}`;
  if (available) {
    const labelPrimary = isKindle ? "Version Kindle sur Amazon" : "Acheter sur Amazon KDP";
    const labelSecondary = isKindle ? "Version Kindle — 9,99 €" : "Acheter le livre — 14,99 €";
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        className={className}
      >
        <ShoppingCart className="w-4 h-4" />
        {isPrimary ? labelPrimary : labelSecondary}
      </a>
    );
  }
  const waitlist =
    "mailto:fiestaman38470@gmail.com?subject=" +
    encodeURIComponent("[Livre] Prévenez-moi à la sortie") +
    "&body=" +
    encodeURIComponent(
      "Bonjour,\n\nMerci de me prévenir dès la sortie du livre 'L'IA au service des enseignants' sur Amazon KDP.\n\n---\nEnvoyé depuis sux1.app/enseignants"
    );
  return (
    <a
      href={waitlist}
      data-testid={testId}
      className={className}
      title="Le livre sort très bientôt — laissez-nous votre email pour être prévenu"
    >
      <Bell className="w-4 h-4" /> Prévenez-moi à la sortie
    </a>
  );
};

/**
 * Google Play badge — compact, inline, brand-safe. Uses the official
 * 4-colour triangle icon via inline SVG so we don't ship a huge PNG asset
 * and the badge adapts to our dark/light theme palette.
 */
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.sux1.app";

const GooglePlayCta = ({ compact = false, testId = "enseignants-play-cta" }) => {
  const padding = compact ? "px-5 py-2.5" : "px-6 py-3";
  return (
    <a
      href={PLAY_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={`inline-flex items-center justify-center gap-3 bg-[#002FA7] text-white ${padding} hover:bg-background hover:text-foreground border-2 border-[#002FA7] transition-colors`}
    >
      <svg
        viewBox="0 0 48 48"
        width="22"
        height="22"
        aria-hidden="true"
        className="shrink-0"
      >
        <path d="M7.2 4.3C6.5 5 6.1 6.1 6.1 7.5V40.5c0 1.4.4 2.5 1.1 3.2L24 24 7.2 4.3z" fill="#00D4FF" />
        <path d="M31.6 17.9L24 24l7.6 6.1 9.3-5.3c1.9-1.1 1.9-3.9 0-5.1l-9.3-5.8z" fill="#FFC107" />
        <path d="M24 24L7.2 43.7c.7.8 1.8.8 3-0 .1-.1.2-.1.3-.2l21.1-12-7.6-7.5z" fill="#E53935" />
        <path d="M24 24L31.6 16.5 10.5 4.5c-.1-.1-.2-.1-.3-.2-1.2-.7-2.3-.6-3 .2L24 24z" fill="#43A047" />
      </svg>
      <div className="flex flex-col items-start leading-tight">
        <span className="font-mono uppercase tracking-[0.14em] text-[9px] opacity-80">
          Disponible sur
        </span>
        <span className="font-bold text-[13px] tracking-tight">
          Google Play
        </span>
      </div>
    </a>
  );
};

const HeroStat = ({ value, label, testid }) => (
  <div className="border-l-2 border-[#FF2A00] pl-3" data-testid={testid}>
    <div
      className="text-3xl font-black"
      style={{ fontFamily: "Outfit" }}
    >
      {value}
    </div>
    <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground mt-1">
      {label}
    </div>
  </div>
);

const CaseCard = ({ n, title, time, teaser, testid }) => (
  <div
    className="border border-border p-5 bg-card hover:border-[#002FA7] transition-colors"
    data-testid={testid}
  >
    <div className="flex items-center gap-2 mb-2">
      <span className="font-mono uppercase tracking-[0.14em] text-[10px] text-[#002FA7]">
        Cas n°{n}
      </span>
      <span className="font-mono text-[10px] text-muted-foreground">·</span>
      <span className="font-mono uppercase tracking-[0.14em] text-[10px] text-[#FF2A00] flex items-center gap-1">
        <Clock className="w-3 h-3" /> {time}
      </span>
    </div>
    <h3
      className="text-lg font-black mb-2"
      style={{ fontFamily: "Outfit" }}
    >
      {title}
    </h3>
    <p className="text-sm text-muted-foreground leading-relaxed">
      {teaser}
    </p>
  </div>
);

export default function Enseignants() {
  useEffect(() => {
    // Persist previous meta values so we can restore them when the
    // user navigates away from the page (React SPA — no per-route
    // HTML swap by default).
    const prevTitle = document.title;
    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        const [key, val] = selector.replace(/[\[\]"]/g, "").split("=");
        el.setAttribute(key, val);
        document.head.appendChild(el);
      }
      const previous = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return () => {
        if (created) el.remove();
        else if (previous !== null) el.setAttribute(attr, previous);
      };
    };

    document.title =
      "L'IA au service des enseignants — 50 cas pratiques | SUX1";
    // Auto-dismiss the Dashboard book promo banner once the user has
    // seen the landing page — no need to keep nudging them.
    try {
      localStorage.setItem("sux1_visited_enseignants", "1");
    } catch (_) {}
    const description =
      "50 cas pratiques d'IA pour gagner 2 heures par jour en tant qu'enseignant. Un livre pratique de 126 pages, écrit pour des profs pressés. Disponible sur Amazon KDP.";
    const ogImage = "https://sux1.app/enseignants/og-image.png";
    const url = "https://sux1.app/enseignants";

    const restorers = [
      setMeta('meta[name="description"]', "content", description),
      setMeta('meta[property="og:title"]', "content",
        "L'IA au service des enseignants — 50 cas pratiques"),
      setMeta('meta[property="og:description"]', "content", description),
      setMeta('meta[property="og:image"]', "content", ogImage),
      setMeta('meta[property="og:url"]', "content", url),
      setMeta('meta[property="og:type"]', "content", "book"),
      setMeta('meta[name="twitter:card"]', "content", "summary_large_image"),
      setMeta('meta[name="twitter:title"]', "content",
        "L'IA au service des enseignants — 50 cas pratiques"),
      setMeta('meta[name="twitter:description"]', "content", description),
      setMeta('meta[name="twitter:image"]', "content", ogImage),
    ];

    // Canonical link
    let canonical = document.head.querySelector('link[rel="canonical"]');
    const prevHref = canonical?.getAttribute("href");
    if (canonical) canonical.setAttribute("href", url);

    return () => {
      document.title = prevTitle;
      restorers.forEach((fn) => fn());
      if (canonical && prevHref) canonical.setAttribute("href", prevHref);
    };
  }, []);

  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ---------- HERO ---------- */}
      <section
        className="border-b border-border"
        data-testid="enseignants-hero"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono uppercase tracking-[0.2em] text-[11px] text-[#002FA7] flex items-center gap-2 mb-4">
              <BookOpen className="w-3 h-3" /> Nouveau livre · KDP 2026
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-5"
              style={{ fontFamily: "Outfit" }}
            >
              L&apos;IA au service des{" "}
              <span className="text-[#002FA7]">enseignants</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mb-6 max-w-2xl leading-relaxed">
              <strong>50 cas pratiques</strong> pour gagner{" "}
              <strong>2 heures par jour</strong>. Un mode d&apos;emploi
              concret, écrit pour des profs pressés — sans jargon
              technique.
            </p>

            <div className="grid grid-cols-3 gap-4 mb-8 max-w-lg">
              <HeroStat value="50" label="cas pratiques" testid="hero-stat-cases" />
              <HeroStat value="5 ch." label="chapitres" testid="hero-stat-chapters" />
              <HeroStat value="126" label="pages" testid="hero-stat-hours" />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
              <KdpCta variant="primary" testId="enseignants-kdp-cta" />
              {KDP_KINDLE_AVAILABLE && (
                <a
                  href={KDP_KINDLE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="enseignants-kdp-kindle-cta"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#002FA7] text-[#002FA7] px-6 py-3 font-mono uppercase tracking-[0.14em] text-[11px] hover:bg-[#002FA7] hover:text-white transition-colors"
                >
                  <ShoppingCart className="w-4 h-4" />
                  Version Kindle — 9,99 €
                </a>
              )}
              <Link
                to={user ? "/" : "/signup"}
                data-testid="enseignants-try-sux1-cta"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#002FA7] px-6 py-3 font-mono uppercase tracking-[0.14em] text-[11px] hover:bg-[#FF2A00] hover:text-white transition-colors"
              >
                Essayer SUX1 gratuit <ArrowRight className="w-4 h-4" />
              </Link>
              <GooglePlayCta testId="enseignants-play-cta-hero" />
            </div>
            <p className="font-mono text-[10px] text-muted-foreground mt-3">
              {KDP_AVAILABLE
                ? "14,99 € broché · 9,99 € Kindle · App Android & web · SUX1 dès 2 € les 20 résumés"
                : "Sortie imminente · 14,99 € broché · App Android & web · SUX1 dès 2 € les 20 résumés"}
            </p>
          </div>

          {/* Book cover + free 5-page preview */}
          <div className="lg:col-span-5 flex flex-col items-center gap-6">
            <div className="relative">
              <div className="absolute inset-0 bg-[#FF2A00] translate-x-3 translate-y-3" />
              <img
                src="/enseignants/cover.jpg"
                alt="Couverture du livre — L'IA au service des enseignants"
                data-testid="enseignants-cover-image"
                className="relative w-full max-w-[320px] shadow-2xl"
                loading="eager"
              />
            </div>

            {/* Free 5-page preview — hook readers before they hit the KDP CTA */}
            <a
              href="/livre-enseignants/extrait-5-pages.pdf"
              target="_blank"
              rel="noopener"
              data-testid="enseignants-preview-cta"
              className="group w-full max-w-[320px] border border-border bg-background hover:border-[#FF2A00] transition-colors p-4 flex items-center gap-3"
            >
              <div className="shrink-0 w-10 h-12 bg-[#002FA7] flex items-center justify-center text-white font-mono text-[10px] font-bold group-hover:bg-[#FF2A00] transition-colors">
                PDF
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-mono uppercase tracking-[0.16em] text-[9px] text-[#FF2A00] mb-1">
                  Extrait gratuit · 5 pages
                </div>
                <div className="text-sm font-bold leading-tight text-foreground">
                  Lire les 5 premières pages
                </div>
                <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">
                  Préface + cas n°1 complet
                </div>
              </div>
              <span className="shrink-0 font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground group-hover:text-[#FF2A00] transition-colors">
                →
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* ---------- PROMISE ---------- */}
      <section className="border-b border-border" data-testid="enseignants-promise">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
          <div className="font-mono uppercase tracking-[0.2em] text-[11px] text-[#002FA7] mb-3">
            Le pari du livre
          </div>
          <h2
            className="text-3xl md:text-4xl font-black tracking-tight mb-6 max-w-3xl"
            style={{ fontFamily: "Outfit" }}
          >
            L&apos;IA ne remplacera pas les profs.<br />
            Elle rendra du temps à ceux qui savent l&apos;utiliser.
          </h2>
          <p className="text-base text-muted-foreground max-w-3xl leading-relaxed">
            Nous avons observé des enseignants dans leur cuisine, le dimanche
            soir, à corriger 60 copies en série. Aux entretiens
            parents-professeurs à 17 h 30, quand ils avaient déjà donné 6 h de
            cours. Aux samedis matins passés à répondre à 25 mails de rentrée.
            Ce livre est pour eux. Pour vous. 50 cas pratiques, cinq
            chapitres, une méthode : garder ce qui fait la valeur du métier,
            déléguer ce qui l&apos;épuise.
          </p>
        </div>
      </section>

      {/* ---------- CHAPTERS ---------- */}
      <section className="border-b border-border" data-testid="enseignants-chapters">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
          <div className="font-mono uppercase tracking-[0.2em] text-[11px] text-[#002FA7] mb-3">
            Table des matières
          </div>
          <h2
            className="text-3xl md:text-4xl font-black tracking-tight mb-8"
            style={{ fontFamily: "Outfit" }}
          >
            Cinq chapitres, cinquante cas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ["1", "Résumer & synthétiser", "10 cas — de la lecture longue au bulletin", "3 h"],
              ["2", "Corriger & évaluer", "10 cas — copies, oral, dyslexie, feedback", "4 h"],
              ["3", "Créer & différencier", "10 cas — séquences, exercices, adaptations", "3 h"],
              ["4", "Communiquer & gérer l'administratif", "10 cas — mails, comptes-rendus, rapports", "2 h"],
              ["5", "Se former, progresser, transmettre", "10 cas — mémoire, concours, formation", "variable"],
            ].map(([n, title, teaser, time]) => (
              <div
                key={n}
                className="border-l-4 border-[#002FA7] pl-5 py-2"
                data-testid={`chapter-${n}`}
              >
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-mono uppercase tracking-[0.14em] text-[10px] text-[#002FA7]">
                    Chapitre {n}
                  </span>
                  <span className="font-mono text-[10px] text-muted-foreground">·</span>
                  <span className="font-mono uppercase tracking-[0.14em] text-[10px] text-[#FF2A00]">
                    ~{time} rendues / sem.
                  </span>
                </div>
                <h3
                  className="text-lg font-black mb-1"
                  style={{ fontFamily: "Outfit" }}
                >
                  {title}
                </h3>
                <p className="text-sm text-muted-foreground">{teaser}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- SAMPLE CASES ---------- */}
      <section className="border-b border-border bg-secondary/20" data-testid="enseignants-samples">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
          <div className="font-mono uppercase tracking-[0.2em] text-[11px] text-[#002FA7] mb-3">
            Extraits gratuits · 3 cas parmi 50
          </div>
          <h2
            className="text-3xl md:text-4xl font-black tracking-tight mb-8"
            style={{ fontFamily: "Outfit" }}
          >
            Trois cas concrets. Testez chez vous.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CaseCard
              n="03"
              title="Corriger 30 copies d'expression écrite en 1 h"
              time="4 h → 1 h"
              teaser="Grille de compétences, notes suggérées, commentaires personnalisés — vous validez, vous ajustez, vous signez. 30 copies corrigées en une soirée au lieu d'un week-end entier."
              testid="sample-case-03"
            />
            <CaseCard
              n="25"
              title="Adapter une séquence pour un élève dyslexique"
              time="1 h 30 économisée"
              teaser="Reformulations phrase à phrase, aération typographique, questions étoilées par niveau. La séquence s'adapte à l'élève sans que vous ayez à tout réécrire."
              testid="sample-case-25"
            />
            <CaseCard
              n="31"
              title="Rédiger un mail délicat à un parent en 3 minutes"
              time="20 min économisées"
              teaser="Ton ferme mais bienveillant, structure factuelle, invitation au dialogue. Le mail sort équilibré du premier coup — vous n'avez qu'à retoucher deux formulations pour retrouver votre voix."
              testid="sample-case-31"
            />
          </div>

          <div className="mt-8 flex items-center gap-4 flex-wrap">
            <Sparkles className="w-5 h-5 text-[#FF2A00]" />
            <div className="text-sm text-muted-foreground">
              47 autres cas dans le livre, dont 10 pour préparer un mémoire de
              M2, un concours interne ou un rendez-vous PPCR.
            </div>
          </div>

          <div className="mt-10 pt-8 border-t border-border">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <div className="font-mono uppercase tracking-[0.2em] text-[11px] text-[#FF2A00] mb-2">
                  Nouveau · Octobre 2026
                </div>
                <div className="text-xl md:text-2xl font-black" style={{ fontFamily: "Outfit" }}>
                  Les 50 cas en 1 clic.
                </div>
                <div className="text-sm text-muted-foreground mt-1 max-w-xl">
                  Page dédiée : {STATS.testable} cas testables directement dans l'app (texte
                  source pré-rempli, format pédagogique adapté), {STATS.prompt} prompts
                  copiables pour votre outil IA habituel.
                </div>
              </div>
              <Link
                to="/enseignants/cas-pratiques"
                data-testid="enseignants-see-50-cases"
                className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.14em] text-[11px] px-6 py-3 bg-[#002FA7] text-white hover:bg-[#FF2A00] transition-colors shrink-0"
              >
                Voir les 50 cas pratiques
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- TESTIMONIAL / QUOTE ---------- */}
      <section className="border-b border-border" data-testid="enseignants-quote">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-14">
          <Quote className="w-8 h-8 text-[#002FA7] mb-4" />
          <blockquote
            className="text-2xl md:text-3xl font-black leading-tight mb-6"
            style={{ fontFamily: "Outfit" }}
          >
            &quot;L&apos;IA ne parle pas à votre place. Elle vous prête sa
            syntaxe.&quot;
          </blockquote>
          <p className="text-sm text-muted-foreground">
            — Un principe qui traverse les 50 cas : l&apos;outil ne remplace
            pas la présence à l&apos;élève, la connaissance intime de la
            classe, la voix. Il enlève ce qui empêche d&apos;y consacrer du
            temps.
          </p>
        </div>
      </section>

      <NewFormatsShowcase audience="enseignants" />

      {/* ---------- WHAT'S INCLUDED ---------- */}
      <section className="border-b border-border" data-testid="enseignants-includes">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
          <div className="font-mono uppercase tracking-[0.2em] text-[11px] text-[#002FA7] mb-3">
            Ce que vous obtenez
          </div>
          <h2
            className="text-3xl md:text-4xl font-black tracking-tight mb-8"
            style={{ fontFamily: "Outfit" }}
          >
            Un livre. Un outil. Un temps rendu.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              [BookOpen, "50 cas pratiques", "Chaque cas : contexte, prompt exact, résultat attendu, astuce pédagogique, variante."],
              [Clock, "Temps gagné indiqué", "Pour chaque cas, une estimation du temps que l'on peut espérer gagner."],
              [Download, "Accès à SUX1", "Exemples gratuits pour découvrir. Ensuite, packs dès 2 € les 20 résumés, sans abonnement."],
              [CornerDownLeft, "Question de suivi", "Sur chaque résumé, un bouton pour enchaîner une question (QCM, mail aux parents, compte rendu, reformulation DYS…) pour seulement 1 crédit les 5 questions. Le workflow prescrit par les 50 cas, en 2 clics."],
            ].map(([Icon, title, body], i) => (
              <div key={i} className="border border-border p-6" data-testid={`include-${i}`}>
                <Icon className="w-6 h-6 text-[#002FA7] mb-3" />
                <h3 className="text-lg font-black mb-2" style={{ fontFamily: "Outfit" }}>
                  {title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- FINAL CTA ---------- */}
      <div className="max-w-4xl mx-auto px-6 lg:px-10 pb-10"><SchoolOfferLink testid="enseignants-school-offer-link" /></div>
      <section className="bg-[#002FA7] text-white" data-testid="enseignants-final-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16 text-center">
          <div className="font-mono uppercase tracking-[0.2em] text-[11px] mb-3 opacity-70">
            Disponible maintenant
          </div>
          <h2
            className="text-3xl md:text-5xl font-black leading-tight mb-6"
            style={{ fontFamily: "Outfit" }}
          >
            Reprenez vos soirées.
          </h2>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            50 cas. 5 chapitres. 126 pages.
            <br />
            À vous, maintenant, de choisir ce que vous en ferez.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
            <KdpCta variant="secondary" testId="enseignants-final-kdp-cta" />
            <KdpCta variant="secondary" testId="enseignants-final-kdp-kindle-cta" format="kindle" />
            <Link
              to={user ? "/" : "/signup"}
              data-testid="enseignants-final-try-sux1"
              className="inline-flex items-center justify-center gap-2 border-2 border-white px-8 py-4 font-mono uppercase tracking-[0.14em] text-[11px] hover:bg-white hover:text-[#002FA7] transition-colors"
            >
              Ou testez SUX1 gratuit <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="enseignants-final-play-cta"
              className="inline-flex items-center justify-center gap-3 bg-white text-[#002FA7] px-8 py-4 hover:bg-[#FF2A00] hover:text-white border-2 border-white transition-colors"
            >
              <svg viewBox="0 0 48 48" width="22" height="22" aria-hidden="true" className="shrink-0">
                <path d="M7.2 4.3C6.5 5 6.1 6.1 6.1 7.5V40.5c0 1.4.4 2.5 1.1 3.2L24 24 7.2 4.3z" fill="#00D4FF" />
                <path d="M31.6 17.9L24 24l7.6 6.1 9.3-5.3c1.9-1.1 1.9-3.9 0-5.1l-9.3-5.8z" fill="#FFC107" />
                <path d="M24 24L7.2 43.7c.7.8 1.8.8 3-0 .1-.1.2-.1.3-.2l21.1-12-7.6-7.5z" fill="#E53935" />
                <path d="M24 24L31.6 16.5 10.5 4.5c-.1-.1-.2-.1-.3-.2-1.2-.7-2.3-.6-3 .2L24 24z" fill="#43A047" />
              </svg>
              <div className="flex flex-col items-start leading-tight">
                <span className="font-mono uppercase tracking-[0.14em] text-[9px] opacity-80">Disponible sur</span>
                <span className="font-bold text-[13px] tracking-tight">Google Play</span>
              </div>
            </a>
          </div>
          <p className="font-mono text-[10px] mt-5 opacity-60">
            Livraison Amazon · Kindle instantané · App Android gratuite · Sans engagement pour SUX1
          </p>
        </div>
      </section>

      {/* Simple footer */}
      <section className="py-6 text-center">
        <p className="font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground">
          <Link to="/" className="hover:text-foreground">
            ← Retour à SUX1
          </Link>
        </p>
      </section>
    </div>
  );
}
